import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "falling-petals";

const DEFAULT_BG_TOP = "#fef3f2";
const DEFAULT_BG_BOTTOM = "#fde7e8";
const DEFAULT_PETAL_A = "#f9a8d4";
const DEFAULT_PETAL_B = "#fb7185";

const PETAL_DENSITY = 0.00012;  // petals per pixel² of canvas area
const MIN_PETALS = 28;
const MAX_PETALS = 90;

function rand(min, max) { return min + Math.random() * (max - min); }

// Draw one petal centred at (0,0) (caller handles translation/rotation).
// The shape is a teardrop made from two cubic curves — soft enough at this
// size to feel like a real petal/leaf rather than a generic ellipse.
function drawPetal(ctx, w, h, color, alpha) {
  ctx.fillStyle = rgba(color, alpha);
  ctx.beginPath();
  ctx.moveTo(0, -h * 0.5);
  ctx.bezierCurveTo(w * 0.6, -h * 0.3, w * 0.5, h * 0.4, 0, h * 0.5);
  ctx.bezierCurveTo(-w * 0.5, h * 0.4, -w * 0.6, -h * 0.3, 0, -h * 0.5);
  ctx.fill();

  // Subtle vein — a single curved highlight line in a slightly darker tone.
  ctx.strokeStyle = rgba(color, alpha * 0.45);
  ctx.lineWidth = 0.6;
  ctx.beginPath();
  ctx.moveTo(0, -h * 0.45);
  ctx.bezierCurveTo(w * 0.05, -h * 0.1, w * 0.05, h * 0.2, 0, h * 0.45);
  ctx.stroke();
}

export default function FallingPetals({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const petalsRef = useRef([]);
  const colorsRef = useRef([DEFAULT_BG_TOP, DEFAULT_BG_BOTTOM, DEFAULT_PETAL_A, DEFAULT_PETAL_B]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG_TOP),
      pickColor(colors, 1, DEFAULT_BG_BOTTOM),
      pickColor(colors, 2, DEFAULT_PETAL_A),
      pickColor(colors, 3, DEFAULT_PETAL_B),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const newPetal = (initialY = false) => {
      const baseSize = Math.min(width, height);
      const sizeFactor = rand(0.018, 0.045);
      const w = baseSize * sizeFactor * 0.65;
      const h = baseSize * sizeFactor;
      return {
        x: rand(-30, width + 30),
        y: initialY ? rand(-50, height) : -rand(20, 80),
        w,
        h,
        rotation: rand(0, Math.PI * 2),
        rotSpeed: rand(-0.02, 0.02),
        vy: rand(0.3, 0.9),
        swayAmp: rand(20, 70),
        swayFreq: rand(0.005, 0.012),
        swayPhase: rand(0, Math.PI * 2),
        slot: Math.random() < 0.5 ? 2 : 3,
        // Tilt ±1 — controls a horizontal "scale" so petals look like they're
        // catching the light from different angles as they tumble.
        tilt: rand(-1, 1),
        tiltSpeed: rand(0.01, 0.03) * (Math.random() < 0.5 ? -1 : 1),
        alpha: rand(0.7, 0.95),
      };
    };

    const seedPetals = () => {
      const target = Math.round(width * height * PETAL_DENSITY);
      const count = Math.max(MIN_PETALS, Math.min(MAX_PETALS, target));
      const arr = new Array(count);
      for (let i = 0; i < count; i += 1) arr[i] = newPetal(true);
      petalsRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedPetals();
    };

    let t = 0;

    const tick = () => {
      const [bgTop, bgBottom, petalA, petalB] = colorsRef.current;

      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, bgTop);
      grad.addColorStop(1, bgBottom);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      const petals = petalsRef.current;
      for (let i = 0; i < petals.length; i += 1) {
        const p = petals[i];
        // Vertical fall + sinusoidal horizontal sway.
        p.y += p.vy;
        const swayX = Math.sin(t * p.swayFreq + p.swayPhase) * p.swayAmp;
        const drawX = p.x + swayX;
        p.rotation += p.rotSpeed;
        p.tilt += p.tiltSpeed;
        if (p.tilt > 1) { p.tilt = 1; p.tiltSpeed *= -1; }
        if (p.tilt < -1) { p.tilt = -1; p.tiltSpeed *= -1; }

        if (p.y - p.h > height) {
          petals[i] = newPetal(false);
          continue;
        }

        ctx.save();
        ctx.translate(drawX, p.y);
        ctx.rotate(p.rotation);
        // Tilt: scaling x by ±cos(t) gives the impression of the petal
        // facing toward / away from the viewer as it rotates in 3D.
        ctx.scale(Math.max(0.3, Math.abs(Math.cos(p.tilt * 1.2))), 1);
        drawPetal(ctx, p.w, p.h, p.slot === 2 ? petalA : petalB, p.alpha);
        ctx.restore();
      }

      t += 1;
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
